/**
 * Alpine.js Lightbox Component
 * Lightweight image lightbox with gallery navigation
 *
 * Usage:
 * <a href="image.jpg" @click.prevent="$store.lightbox.open($event.currentTarget.href, 0, images)">
 *   <img src="thumb.jpg" alt="Image">
 * </a>
 */

const lightboxStore = {
  // State
  isOpen: false,
  currentIndex: 0,
  images: [],
  currentImage: '',

  // Zoom state
  scale: 1,
  translateX: 0,
  translateY: 0,
  isDragging: false,
  dragStartX: 0,
  dragStartY: 0,
  dragOffsetX: 0,
  dragOffsetY: 0,

  // Smooth drag state
  velocity: { x: 0, y: 0 },
  lastMoveTime: 0,
  lastMoveX: 0,
  lastMoveY: 0,

  // Open lightbox
  open(imageSrc, index = 0, gallery = []) {
    this.currentImage = imageSrc;
    this.currentIndex = index;
    this.images = gallery.length > 0 ? gallery : [imageSrc];
    this.isOpen = true;
    this.resetZoom();
    document.body.classList.add('overflow-hidden');
  },

  // Close lightbox
  close() {
    this.isOpen = false;
    this.resetZoom();
    document.body.classList.remove('overflow-hidden');
  },

  // Navigate to next image
  next() {
    if (this.images.length <= 1) return;
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
    this.currentImage = this.images[this.currentIndex];
    this.resetZoom();
  },

  // Navigate to previous image
  previous() {
    if (this.images.length <= 1) return;
    this.currentIndex = this.currentIndex === 0 ? this.images.length - 1 : this.currentIndex - 1;
    this.currentImage = this.images[this.currentIndex];
    this.resetZoom();
  },

  // Reset zoom
  resetZoom() {
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.isDragging = false;
  },

  // Toggle zoom on click
  toggleZoom(event) {
    if (this.scale === 1) {
      // Zoom in to 2x
      this.scale = 2;
    } else {
      // Reset zoom
      this.resetZoom();
    }
  },

  // Handle mouse wheel zoom
  handleWheel(event) {
    event.preventDefault();

    const delta = event.deltaY * -0.01;
    const newScale = Math.min(Math.max(1, this.scale + delta), 5);

    if (newScale === 1) {
      this.resetZoom();
    } else {
      this.scale = newScale;
    }
  },

  // Get drag boundaries based on zoom level
  getBoundaries() {
    if (this.scale <= 1) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };

    // Calculate how much the image has expanded beyond viewport
    const expandRatio = this.scale - 1;
    const maxTranslate = 300 * expandRatio; // Limit based on zoom level

    return {
      minX: -maxTranslate,
      maxX: maxTranslate,
      minY: -maxTranslate,
      maxY: maxTranslate
    };
  },

  // Constrain translation within boundaries
  constrainTranslation(x, y) {
    const bounds = this.getBoundaries();
    return {
      x: Math.max(bounds.minX, Math.min(bounds.maxX, x)),
      y: Math.max(bounds.minY, Math.min(bounds.maxY, y))
    };
  },

  // Start dragging
  startDrag(event) {
    if (this.scale <= 1) return;

    this.isDragging = true;
    this.dragStartX = event.clientX - this.translateX;
    this.dragStartY = event.clientY - this.translateY;
    this.lastMoveX = event.clientX;
    this.lastMoveY = event.clientY;
    this.lastMoveTime = Date.now();
    this.velocity = { x: 0, y: 0 };
  },

  // Drag image with smooth tracking
  drag(event) {
    if (!this.isDragging || this.scale <= 1) return;

    event.preventDefault();

    const now = Date.now();
    const deltaTime = now - this.lastMoveTime;

    if (deltaTime > 0) {
      // Calculate velocity for momentum
      this.velocity.x = (event.clientX - this.lastMoveX) / deltaTime * 16; // Normalize to 60fps
      this.velocity.y = (event.clientY - this.lastMoveY) / deltaTime * 16;
    }

    // Calculate new position
    const newX = event.clientX - this.dragStartX;
    const newY = event.clientY - this.dragStartY;

    // Apply boundaries
    const constrained = this.constrainTranslation(newX, newY);
    this.translateX = constrained.x;
    this.translateY = constrained.y;

    // Update tracking
    this.lastMoveX = event.clientX;
    this.lastMoveY = event.clientY;
    this.lastMoveTime = now;
  },

  // End dragging with momentum
  endDrag() {
    if (!this.isDragging) return;

    this.isDragging = false;

    // Apply momentum/inertia effect
    const applyMomentum = () => {
      if (Math.abs(this.velocity.x) < 0.1 && Math.abs(this.velocity.y) < 0.1) {
        return; // Momentum has stopped
      }

      // Apply velocity
      const newX = this.translateX + this.velocity.x;
      const newY = this.translateY + this.velocity.y;

      // Apply boundaries
      const constrained = this.constrainTranslation(newX, newY);
      this.translateX = constrained.x;
      this.translateY = constrained.y;

      // Apply friction
      this.velocity.x *= 0.92;
      this.velocity.y *= 0.92;

      // Continue animation
      requestAnimationFrame(applyMomentum);
    };

    // Only apply momentum if velocity is significant
    if (Math.abs(this.velocity.x) > 0.5 || Math.abs(this.velocity.y) > 0.5) {
      requestAnimationFrame(applyMomentum);
    }
  },

  // Initialize
  init() {
    // Keyboard navigation is now handled in the template using @keydown.window directives
    // This is the Alpine.js recommended approach for better reactivity

    // Auto-setup lightbox links with data-lightbox attribute
    // Use CAPTURE PHASE (true) to run before other handlers
    document.addEventListener('click', (e) => {
      const link = e.target.closest('[data-lightbox]');
      if (!link) return;

      e.preventDefault();
      e.stopPropagation();

      const gallery = link.dataset.lightboxGallery;
      let images = [link.href];
      let index = 0;

      // If part of a gallery, collect all images
      if (gallery) {
        const galleryLinks = document.querySelectorAll(`[data-lightbox-gallery="${gallery}"]`);
        images = Array.from(galleryLinks).map(a => a.href);
        index = Array.from(galleryLinks).indexOf(link);
      }

      this.open(link.href, index, images);
    }, true); // CAPTURE PHASE - runs before other handlers

  },
};

export default lightboxStore;
